import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Shield, 
  Cookie, 
  FileText, 
  AlertTriangle,
  ArrowLeft,
  Home,
  Mail,
  CheckCircle,
  XCircle,
  Scale,
  CreditCard,
  HelpCircle,
  Bot,
  RefreshCw,
  Ban
} from 'lucide-react';

export const LegalPage = () => {
  const { page } = useParams();
  const navigate = useNavigate();
  // Derive activeTab from page param directly instead of using state
  const activeTab = page || 'privacy';
  
  const handleTabChange = (value) => {
    navigate(`/legal/${value}`, { replace: true });
  };

  const lastUpdated = 'January 2026';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Link to="/" className="flex items-center gap-2">
              <img src="/harmonix-logo.png" alt="Harmonix" className="h-8" />
            </Link>
          </div>
          <Link 
            to="/dashboard" 
            className="text-slate-600 hover:text-blue-600 flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <Home className="w-4 h-4" />
            Dashboard
          </Link>
        </div>
      </div>
      
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Page Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 text-xs font-semibold tracking-widest uppercase rounded-full text-blue-700 bg-blue-100 border border-blue-200">
            <Scale className="w-3.5 h-3.5" />
            Legal
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Legal & Policies</h1>
          <p className="text-slate-600">Last updated: {lastUpdated}</p>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="bg-slate-100 border border-slate-200 p-1 flex-wrap justify-center">
            <TabsTrigger value="privacy" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Shield className="w-4 h-4 mr-2" />Privacy
            </TabsTrigger>
            <TabsTrigger value="terms" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" />Terms
            </TabsTrigger>
            <TabsTrigger value="cookies" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Cookie className="w-4 h-4 mr-2" />Cookies
            </TabsTrigger>
            <TabsTrigger value="refund" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <RefreshCw className="w-4 h-4 mr-2" />Refunds
            </TabsTrigger>
            <TabsTrigger value="acceptable-use" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Ban className="w-4 h-4 mr-2" />Acceptable Use
            </TabsTrigger>
            <TabsTrigger value="billing-faq" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <HelpCircle className="w-4 h-4 mr-2" />Billing FAQ
            </TabsTrigger>
          </TabsList>
          
          {/* Privacy Policy */}
          <TabsContent value="privacy">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-8 prose prose-slate max-w-none">
                <div className="flex items-center gap-3 mb-6 not-prose">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Privacy Policy</h2>
                    <p className="text-slate-500 text-sm">Last updated: {lastUpdated}</p>
                  </div>
                </div>
                
                <p className="lead text-lg text-slate-700">
                  Harmonix (&quot;Harmonix&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;) provides an AI-assisted communication and governance platform for organizations. This Privacy Policy explains how we collect, use, disclose, and protect personal data when you use our services.
                </p>
                
                <h3>1. Information We Collect</h3>
                <p>We may collect the following categories of information:</p>
                
                <h4>a. Account & Identity Information</h4>
                <ul>
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Organization name</li>
                  <li>User role and permissions</li>
                  <li>Authentication identifiers (including SSO where enabled)</li>
                </ul>
                
                <h4>b. Usage & Technical Information</h4>
                <ul>
                  <li>Log data (timestamps, interactions, configuration actions)</li>
                  <li>IP address, browser type, device information</li>
                  <li>Cookies and similar technologies</li>
                </ul>
                
                <h4>c. Customer Content</h4>
                <ul>
                  <li>Policies, documents, messages, and configurations uploaded or connected by customers</li>
                  <li>Metadata generated through use of the platform</li>
                </ul>
                
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 my-4 not-prose">
                  <div className="flex items-center gap-2 text-emerald-700 font-medium">
                    <CheckCircle className="w-5 h-5" />
                    We do not sell personal data.
                  </div>
                </div>
                
                <h3>2. How We Use Information</h3>
                <p>We process information to:</p>
                <ul>
                  <li>Provide, operate, and maintain the Harmonix platform</li>
                  <li>Authenticate users and enforce access controls</li>
                  <li>Generate AI-assisted responses based on customer-provided content</li>
                  <li>Improve platform performance, security, and reliability</li>
                  <li>Comply with legal and contractual obligations</li>
                </ul>
                
                <h3>3. AI & Automated Processing</h3>
                <p>Harmonix uses automated systems and artificial intelligence to process customer-provided data and generate responses.</p>
                <p><strong>AI outputs:</strong></p>
                <ul>
                  <li>Are generated solely based on configured rules, permissions, and connected data</li>
                  <li>Are informational only and do not constitute legal, financial, or professional advice</li>
                  <li>Should be reviewed by customers before being relied upon for decision-making</li>
                </ul>
                
                <h3>4. Data Sharing & Sub-Processors</h3>
                <p>We may share data with trusted third-party service providers, including:</p>
                <ul>
                  <li>Cloud hosting providers</li>
                  <li>Authentication and identity providers</li>
                  <li>Analytics and monitoring services</li>
                  <li>Payment providers (including Stripe)</li>
                </ul>
                <p>All sub-processors are bound by contractual data protection and confidentiality obligations.</p>
                
                <h3>5. Data Retention</h3>
                <p>We retain personal data only as long as necessary to:</p>
                <ul>
                  <li>Provide the services</li>
                  <li>Meet legal, accounting, or regulatory requirements</li>
                </ul>
                <p>Customer data is deleted or anonymized after account termination, subject to legal obligations.</p>
                
                <h3>6. Security Measures</h3>
                <p>We implement reasonable technical and organizational measures including:</p>
                <ul>
                  <li>Encryption in transit</li>
                  <li>Role-based access control</li>
                  <li>Logical tenant isolation</li>
                  <li>Audit and activity logging</li>
                </ul>
                
                <h3>7. Your Rights</h3>
                <p>Depending on your jurisdiction, you may have rights to access, correct, or delete personal data. Requests can be submitted to:</p>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 my-4 not-prose">
                  <div className="flex items-center gap-2 text-slate-700">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <a href="mailto:privacy@harmonix.info" className="text-blue-600 hover:underline">privacy@harmonix.info</a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Terms of Service */}
          <TabsContent value="terms">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-8 prose prose-slate max-w-none">
                <div className="flex items-center gap-3 mb-6 not-prose">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Terms of Service</h2>
                    <p className="text-slate-500 text-sm">Last updated: {lastUpdated}</p>
                  </div>
                </div>
                
                <h3>Billing & Payments</h3>
                <p>Payments for Harmonix subscriptions are processed by <strong>Stripe</strong>, our payment processor.</p>
                <p>By purchasing a subscription:</p>
                <ul>
                  <li>You agree to Stripe&apos;s terms and privacy policy</li>
                  <li>Applicable taxes may be collected based on your jurisdiction</li>
                  <li>Billing descriptors will reference Harmonix or our payment processor</li>
                </ul>
                
                <h3>Subscriptions & Renewals</h3>
                <ul>
                  <li>Subscriptions are billed on a recurring basis unless canceled before the renewal date</li>
                  <li>Access to the service continues until the end of the current billing period</li>
                </ul>
                
                <h3>Refund Policy</h3>
                <p>Refunds are handled in accordance with our <a href="/refund" className="text-blue-600 hover:underline">Refund Policy</a>.</p>
                
                <h3>Termination</h3>
                <p>We may suspend or terminate access if:</p>
                <ul>
                  <li>These terms are violated</li>
                  <li>Payment obligations are not met</li>
                  <li>Use of the service poses legal or security risks</li>
                </ul>
                <p>Upon termination, access to the platform will cease.</p>
                
                <h3>AI Use & Disclaimer</h3>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4 not-prose">
                  <div className="flex items-start gap-3">
                    <Bot className="w-6 h-6 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 mb-2">Important AI Disclosures</p>
                      <ul className="text-sm text-amber-700 space-y-1">
                        <li>• AI-generated responses are informational only</li>
                        <li>• Outputs do not constitute legal, financial, regulatory, or professional advice</li>
                        <li>• Harmonix does not guarantee the accuracy, completeness, or applicability of AI outputs</li>
                        <li>• Customers remain fully responsible for reviewing and validating AI responses before relying on them</li>
                      </ul>
                      <p className="text-sm text-amber-700 mt-3 font-medium">
                        Harmonix is a decision-support and communication tool, not a substitute for human judgment or professional services.
                      </p>
                    </div>
                  </div>
                </div>
                
                <h3>Disclaimer of Warranties</h3>
                <p>The service is provided &quot;as is&quot; and &quot;as available&quot;. We do not guarantee that:</p>
                <ul>
                  <li>AI responses will be error-free</li>
                  <li>The service will meet all customer expectations</li>
                  <li>Outputs will be suitable for legal or compliance decisions</li>
                </ul>
                
                <h3>Limitation of Liability</h3>
                <p>To the maximum extent permitted by law, Harmonix shall not be liable for indirect, incidental, or consequential damages.</p>
                
                <h3>Data Processing Agreement</h3>
                <p>This Data Processing Agreement (&quot;DPA&quot;) forms part of these Terms of Service.</p>
                
                <h4>Roles</h4>
                <ul>
                  <li><strong>Customer:</strong> Data Controller</li>
                  <li><strong>Harmonix:</strong> Data Processor</li>
                </ul>
                
                <h4>Scope of Processing</h4>
                <p>Harmonix processes personal data solely for the purpose of providing the services described in the subscription agreement.</p>
                
                <h4>Processor Obligations</h4>
                <p>Harmonix agrees to:</p>
                <ul>
                  <li>Process personal data only on documented customer instructions</li>
                  <li>Ensure personnel confidentiality</li>
                  <li>Implement appropriate technical and organizational safeguards</li>
                  <li>Assist customers with data subject requests where legally required</li>
                  <li>Notify customers of personal data breaches without undue delay</li>
                </ul>
                
                <h4>Sub-Processors</h4>
                <p>Harmonix may engage sub-processors to provide infrastructure and services. A list of sub-processors will be provided upon request.</p>
                
                <h4>International Transfers</h4>
                <p>Where data is transferred internationally, Harmonix will ensure appropriate safeguards are in place in accordance with applicable data protection laws.</p>
                
                <h3>General Provisions</h3>
                
                <h4>Acceptance of Terms</h4>
                <p>By accessing or using the Harmonix platform, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service, including all policies, guidelines, and agreements incorporated herein by reference, as may be amended, modified, supplemented, or updated from time to time at our sole discretion. Your continued use of the service following any such modifications constitutes your acceptance of the revised terms, whether or not you have reviewed such modifications. It is your responsibility to review these terms periodically for changes.</p>
                
                <h4>Amendments and Modifications</h4>
                <p>Harmonix reserves the right, at its sole and absolute discretion, to amend, modify, supplement, or replace any provision of these Terms of Service, the Privacy Policy, and any other operational policies, guidelines, or agreements at any time and without prior notice to you. Such amendments shall become effective immediately upon posting to the platform or, where applicable, upon notification through the service interface, email communication to the address associated with your account, or any other reasonable means of communication. Your continued access to or use of the platform after any such amendments have been made shall constitute your binding acceptance of such amended terms. Harmonix shall not be liable to you or any third party for any modification, suspension, or discontinuance of these terms or the service.</p>
                
                <h4>Data Security and Shared Information</h4>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Notwithstanding any representations, warranties, or statements made elsewhere regarding security measures implemented by Harmonix, the Customer acknowledges and agrees that any data, information, content, materials, or communications (collectively, &quot;Shared Information&quot;) transmitted to, through, or stored on the platform is provided at the Customer&apos;s sole risk. To the fullest extent permitted by applicable law, Harmonix expressly disclaims any and all liability, responsibility, or obligation with respect to the security, confidentiality, integrity, availability, or preservation of any Shared Information. The Customer assumes full and complete responsibility for implementing appropriate security measures, backup procedures, and safeguards for any Shared Information prior to transmission to or storage on the platform. Harmonix shall not be liable for any unauthorized access, use, modification, destruction, loss, theft, or disclosure of Shared Information, regardless of whether such event results from the actions or omissions of Harmonix, its employees, contractors, agents, third-party service providers, or any other party. The Customer agrees to indemnify, defend, and hold harmless Harmonix from and against any claims, damages, losses, or expenses arising from or related to the security or handling of Shared Information.
                </p>
                
                <h4>Governing Law and Dispute Resolution</h4>
                <p>These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, USA. Any disputes shall be resolved through binding arbitration.</p>
                
                <h4>Severability</h4>
                <p>If any provision of these Terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.</p>
                
                <h4>Entire Agreement</h4>
                <p className="text-slate-600 text-sm">
                  These Terms of Service, together with the Privacy Policy, Cookie Policy, Refund Policy, Data Processing Agreement, Acceptable Use Policy, and any other policies, guidelines, or agreements referenced herein or otherwise made available through the platform (collectively, the &quot;Agreement&quot;), constitute the entire agreement between you and Harmonix with respect to your use of the service and supersede all prior or contemporaneous communications, representations, understandings, and agreements, whether oral or written. By using the service, you represent and warrant that you have the legal capacity to enter into this Agreement and that you accept and agree to be bound by all terms, conditions, and policies contained herein and as may be subsequently modified pursuant to Section &quot;Amendments and Modifications&quot; above. You further acknowledge that Harmonix may establish additional policies, guidelines, or operational procedures at any time, and your continued use of the service shall constitute acceptance of and agreement to such additional terms.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Cookie Policy */}
          <TabsContent value="cookies">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-8 prose prose-slate max-w-none">
                <div className="flex items-center gap-3 mb-6 not-prose">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Cookie className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Cookie Policy</h2>
                    <p className="text-slate-500 text-sm">Last updated: {lastUpdated}</p>
                  </div>
                </div>
                
                <p className="lead text-lg text-slate-700">
                  Harmonix uses cookies and similar technologies to ensure platform functionality and improve user experience.
                </p>
                
                <h3>Types of Cookies We Use</h3>
                
                <div className="grid md:grid-cols-3 gap-4 my-6 not-prose">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-slate-900 mb-2">Essential Cookies</h4>
                    <p className="text-sm text-slate-600">Required for authentication, security, and session management.</p>
                  </div>
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <h4 className="font-semibold text-slate-900 mb-2">Functional Cookies</h4>
                    <p className="text-sm text-slate-600">Store user preferences and settings.</p>
                  </div>
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <h4 className="font-semibold text-slate-900 mb-2">Performance Cookies</h4>
                    <p className="text-sm text-slate-600">Collect aggregated, anonymized usage statistics to improve the service.</p>
                  </div>
                </div>
                
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 my-4 not-prose">
                  <div className="flex items-center gap-2 text-emerald-700 font-medium">
                    <CheckCircle className="w-5 h-5" />
                    We do not use cookies for behavioral advertising or third-party marketing.
                  </div>
                </div>
                
                <h3>Managing Cookies</h3>
                <p>Users can manage cookies through browser settings. Disabling essential cookies may prevent access to the service.</p>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Refund Policy */}
          <TabsContent value="refund">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-8 prose prose-slate max-w-none">
                <div className="flex items-center gap-3 mb-6 not-prose">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <RefreshCw className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Harmonix Refund Policy</h2>
                    <p className="text-slate-500 text-sm">Last updated: January 2026</p>
                  </div>
                </div>
                
                <p className="lead text-lg text-slate-700">
                  Payments for Harmonix subscriptions are processed by Stripe, our payment processor.
                </p>
                
                <h3>Refund Eligibility</h3>
                <p>Customers are eligible for a full refund within 14 days of their initial purchase.</p>
                
                <h3>After the Refund Window</h3>
                <p>After the 14-day refund period has passed:</p>
                <ul>
                  <li>Subscription fees are non-refundable</li>
                  <li>Partial billing periods are not refunded</li>
                  <li>Cancellations stop future charges but do not result in refunds for past payments</li>
                </ul>
                
                <h3>How to Request a Refund</h3>
                <p>Refund requests can be made by:</p>
                <ul>
                  <li>Emailing Harmonix support at <a href="mailto:support@harmonix.info">support@harmonix.info</a></li>
                </ul>
                <p>Refunds are processed using the original payment method.</p>
                
                <h3>Renewals</h3>
                <p>Renewal payments are not eligible for refunds unless required under applicable consumer protection laws.</p>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Acceptable Use Policy */}
          <TabsContent value="acceptable-use">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-8 prose prose-slate max-w-none">
                <div className="flex items-center gap-3 mb-6 not-prose">
                  <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                    <Ban className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Acceptable Use Policy</h2>
                    <p className="text-slate-500 text-sm">Last updated: {lastUpdated}</p>
                  </div>
                </div>
                
                <p className="lead text-lg text-slate-700">
                  You agree not to use Harmonix to:
                </p>
                
                <div className="space-y-3 my-6 not-prose">
                  {[
                    'Violate any applicable laws or regulations',
                    'Infringe intellectual property or privacy rights',
                    'Share confidential or personal data without authorization',
                    'Circumvent access controls or security mechanisms',
                    'Upload or distribute malware or harmful code',
                    'Generate or disseminate misleading, fraudulent, or unlawful content',
                    'Represent AI-generated responses as professional advice'
                  ].map((item) => (
                    <div key={`aup-${item}`} className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4 not-prose">
                  <div className="flex items-start gap-2 text-amber-700">
                    <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span><strong>Harmonix reserves the right to suspend or terminate accounts for violations</strong> of this Acceptable Use Policy.</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Billing FAQ */}
          <TabsContent value="billing-faq">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-8 prose prose-slate max-w-none">
                <div className="flex items-center gap-3 mb-6 not-prose">
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <HelpCircle className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Billing & Payments FAQ</h2>
                    <p className="text-slate-500 text-sm">Last updated: {lastUpdated}</p>
                  </div>
                </div>
                
                <div className="space-y-6 not-prose">
                  {[
                    {
                      q: 'Who processes my payment?',
                      a: 'All payments are securely processed by Stripe, our authorized payment processor.'
                    },
                    {
                      q: 'Why does my statement mention Stripe?',
                      a: 'Stripe handles billing, tax calculation, invoicing, and compliance on behalf of Harmonix. Your card or bank statement will reference Stripe or Harmonix.'
                    },
                    {
                      q: 'What payment methods are supported?',
                      a: 'Payment methods depend on your location and are presented by Stripe at checkout. These may include credit/debit cards and local payment options.'
                    },
                    {
                      q: 'Are taxes included?',
                      a: 'Applicable taxes (such as VAT or sales tax) are calculated and collected where required by law.'
                    },
                    {
                      q: 'How do I cancel my subscription?',
                      a: 'You can cancel your subscription at any time from your account settings. Access remains active until the end of the current billing period.'
                    },
                    {
                      q: 'Can I get a refund?',
                      a: 'Customers are entitled to a full refund within 14 days of their initial purchase. After that period, subscription fees are non-refundable. Please see our Refund Policy for full details.'
                    },
                    {
                      q: 'Is my payment information stored by Harmonix?',
                      a: 'No. Harmonix does not store or process your payment card information. All payment data is handled securely by Stripe.'
                    }
                  ].map((faq) => (
                    <div key={`billing-faq-${faq.q}`} className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <h4 className="font-semibold text-slate-900 mb-2 flex items-start gap-2">
                        <CreditCard className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                        {faq.q}
                      </h4>
                      <p className="text-slate-600 ml-7">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-slate-200 text-center text-slate-500 text-sm">
          <p className="mb-4">Questions about our policies?</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="mailto:legal@harmonix.info" className="text-blue-600 hover:underline">legal@harmonix.info</a>
            <span>•</span>
            <a href="mailto:privacy@harmonix.info" className="text-blue-600 hover:underline">privacy@harmonix.info</a>
            <span>•</span>
            <a href="mailto:support@harmonix.info" className="text-blue-600 hover:underline">support@harmonix.info</a>
          </div>
          <p className="mt-6">© 2026 Harmonix. All rights reserved.</p>
          <p className="mt-1 text-slate-400">Powered by VELORIEN GROUP LLC</p>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
